import ReportQuotationsHeader from '@/components/Reports/ReportPrivate/ReportQuotations/ReportQuotationsHeader';
import { useQuotationReportItemsBySuppliers } from '@/core/domain/stock/Quotations/Quotations.hooks';
import {
  ReportQuotationItem,
  ReportQuotationType,
} from '@/core/domain/stock/Quotations/Quotations.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { getTableScrollX } from '@/utils/table';
import { Button, Space, Statistic, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { Suspense, lazy, useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

type SearchParams = {
  quotationId?: string;
  type?: ReportQuotationType;
  order?: 'items' | 'lessPrice';
};

const PrivateQuatationReportsItemsSuppliers: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();
  const params = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;

  const { data, isLoading } = useQuotationReportItemsBySuppliers({
    quotationId: Number(params.quotationId),
    type: params.type,
  });

  useEffect(() => {
    if (data?.result?.company) {
      document.title = `${
        params.type === ReportQuotationType.ItemsSupplier
          ? 'Itens_Fornecedores'
          : 'Fornecedores_Itens'
      }-${data.result?.company.name?.split(' ').join('_')}`;
    }
  }, [data?.result?.company]);

  let quotations = data?.result?.quotation || [];

  if (params.order === 'lessPrice') {
    quotations = quotations?.map((item) => ({
      ...item,
      items: item.items.sort((a, b) => a.total - b.total),
    }));
  }

  const columns: ColumnsType<ReportQuotationItem> = [
    {
      dataIndex: 'code',
      title: 'Código',
      width: 60,
      render: (code) => code ?? '-',
    },
    {
      dataIndex: 'description',
      width: 200,
      title:
        params.type === ReportQuotationType.ItemsSupplier
          ? 'Fornecedor'
          : 'Descrição',
      ellipsis: true,
    },
    {
      dataIndex: 'requestedQuantity',
      title: 'Quantidade',
      width: 80,
      align: 'right',
      render: (value) => formatterPrice(value),
    },
    {
      dataIndex: 'suppliedQuantity',
      title: 'Qtde. fornecida',
      align: 'right',
      width: 100,
      render: (value) => formatterPrice(value),
    },
    {
      dataIndex: 'unitPrice',
      title: 'Valor unitário($)',
      align: 'right',
      width: 100,
      render: (value) => formatterPrice(value),
    },
    {
      title: 'Valor total($)',
      align: 'right',
      width: 100,
      render: (_, row) =>
        formatterPrice((row.suppliedQuantity || 0) * (row.unitPrice || 0)),
    },
  ];

  return (
    <Suspense fallback={<>Carregando...</>}>
      {match.isExact && !isLoading && !!data && (
        <TemplateReport>
          <>
            <div className="btn-print">
              <Button type="primary" ghost onClick={() => window.print()}>
                Imprimir documento
              </Button>
            </div>

            <main className="reportQuotation" onLoad={() => window.print()}>
              <Space direction="vertical" className="w-100" size="large">
                <ReportQuotationsHeader
                  company={data?.result?.company}
                  title={
                    params.type === ReportQuotationType.ItemsSupplier
                      ? 'Itens/Fornecedores'
                      : 'Fornecedores/Itens'
                  }
                />

                <div className="card">
                  <Statistic
                    title="Cotação"
                    valueRender={() => <span>{params.quotationId}</span>}
                  />

                  {quotations.map((quotation, index) => (
                    <div key={index} className="m-t-20">
                      <big>
                        <span>{quotation.name}</span>
                      </big>
                      <Table
                        dataSource={quotation.items}
                        columns={columns}
                        pagination={false}
                        scroll={{ x: getTableScrollX(columns) }}
                      />
                    </div>
                  ))}
                </div>
              </Space>
            </main>
          </>
        </TemplateReport>
      )}
    </Suspense>
  );
};

export default PrivateQuatationReportsItemsSuppliers;
